import { useMemo, useState } from 'react'
import { cn } from '@/lib/utils'
import type { Milestone } from '@/features/projects'
import { PHASE_BAR, PHASE_LABEL, longDate, shortDate, type CycleRow, type PhaseRow } from './model'

const DAY = 86_400_000

interface Bar {
  key: string
  label: string
  sub: string
  start: number
  end: number
  open: boolean
  tone: string
  slipped: boolean
  status: PhaseRow['status']
  gate: { at: number; met: boolean } | null
}

/**
 * The project's Gantt: one row per phase, grouped by cycle, each cycle adding its own bars under
 * the same project. Today's line is drawn, a bar that has run past its end without its gate
 * turns red, gates are diamonds at a phase's end, and milestones sit on their own row.
 */
export function LifecycleGantt({ phases, cycles, milestones, target }: { phases: PhaseRow[]; cycles: CycleRow[]; milestones: Milestone[]; target: string | null }) {
  const [hover, setHover] = useState<string | null>(null)
  const [now] = useState(() => Date.now())
  const { bars, from, to } = useMemo(() => {
    const shown = phases.filter((p) => p.status !== 'skipped')
    const ends = shown.map((p) => (p.endAt ? Date.parse(p.endAt) : now))
    const starts = shown.map((p) => Date.parse(p.startAt))
    const ms = milestones.filter((m) => m.dueAt).map((m) => Date.parse(m.dueAt!))
    const t = target ? Date.parse(target) : now
    const lo = Math.min(now, ...starts, ...ms) - 3 * DAY
    const hi = Math.max(now, t, ...ends, ...ms) + 21 * DAY
    const linked = cycles.filter((c) => c.projectId).map((c) => c.n).sort((x, y) => x - y)
    const out: Bar[] = shown
      .slice()
      .sort((a, b) => a.cycle - b.cycle || Date.parse(a.startAt) - Date.parse(b.startAt))
      .map((p) => {
        const start = Date.parse(p.startAt)
        const end = p.endAt ? Date.parse(p.endAt) : p.status === 'done' ? start : hi
        const slipped = (p.status === 'active' || p.status === 'upcoming') && p.endAt !== null && Date.parse(p.endAt) < now
        const cycle = cycles.find((c) => c.n === p.cycle)
        const cycleName = cycle?.name
        // A migrated Additional Requirements project is its own cycle: name the bar after it, numbered in order.
        const reqNo = cycle?.projectId ? linked.indexOf(cycle.n) + 1 : 0
        return {
          key: p.id,
          label: reqNo && cycleName ? cycleName : PHASE_LABEL[p.phase],
          sub: reqNo ? `Additional requirement #${reqNo}` : p.phase === 'post_sales' ? 'Ongoing' : p.cycle > 1 ? `Cycle ${p.cycle}${cycleName ? ` · ${cycleName}` : ''}` : 'Cycle 1',
          start,
          end: Math.max(end, start + DAY / 2),
          open: !p.endAt,
          tone: PHASE_BAR[p.phase],
          slipped,
          status: p.status,
          gate: p.phase === 'post_sales' ? null : { at: p.gate ? Date.parse(p.gate.metAt) : end, met: Boolean(p.gate) },
        }
      })
    return { bars: out, from: lo, to: hi }
  }, [phases, cycles, milestones, target, now])

  const span = Math.max(DAY, to - from)
  const pct = (at: number) => `${((at - from) / span) * 100}%`
  const weeks: number[] = []
  const first = new Date(from)
  first.setHours(0, 0, 0, 0)
  first.setDate(first.getDate() + ((8 - first.getDay()) % 7))
  for (let w = first.getTime(); w < to; w += 7 * DAY) weeks.push(w)
  const months = weeks.filter((w, i) => i === 0 || new Date(w).getMonth() !== new Date(weeks[i - 1]!).getMonth())

  return (
    <div className="overflow-x-auto">
      <div className="min-w-[46rem]">
        <div className="relative ml-44 h-6 border-b border-border text-tiny text-text-muted">
          {months.map((m) => (
            <span key={m} className="absolute top-0 whitespace-nowrap font-medium text-muted-foreground" style={{ left: pct(m) }}>
              {new Date(m).toLocaleDateString(undefined, { month: 'short', year: '2-digit' })}
            </span>
          ))}
        </div>
        <div className="relative">
          {/* Week lines, today, and the target, across every row. */}
          <div className="pointer-events-none absolute inset-y-0 left-44 right-0">
            {weeks.map((w) => <span key={w} className="absolute inset-y-0 w-px bg-border-soft" style={{ left: pct(w) }} />)}
            {target ? <span title={`Target Post-Sales ${longDate(target)}`} className="absolute inset-y-0 w-0 border-l-2 border-dashed border-brand" style={{ left: pct(Date.parse(target)) }} /> : null}
            <span title="Today" className="absolute inset-y-0 w-0.5 bg-danger" style={{ left: pct(now) }} />
          </div>
          {bars.map((b) => (
            <div key={b.key} className="relative flex h-9 items-center border-b border-border-soft">
              <div className="w-44 shrink-0 pr-2">
                <p className="truncate text-sm font-medium">{b.label}</p>
                <p className="truncate text-tiny text-text-muted">{b.sub}</p>
              </div>
              <div className="relative h-full flex-1">
                <div
                  role="img"
                  aria-label={`${b.label}, ${b.sub}: ${longDate(new Date(b.start).toISOString())} to ${b.open ? 'ongoing' : longDate(new Date(b.end).toISOString())}${b.slipped ? ', past its end date' : ''}`}
                  onMouseEnter={() => setHover(b.key)}
                  onMouseLeave={() => setHover(null)}
                  className={cn(
                    'absolute top-2 h-5 rounded-sm',
                    b.slipped ? 'bg-danger' : b.tone,
                    b.status === 'done' && 'opacity-60',
                    b.status === 'upcoming' && !b.slipped && 'opacity-80',
                    b.open && 'rounded-r-none',
                  )}
                  style={{ left: pct(b.start), width: `calc(${pct(b.end)} - ${pct(b.start)})` }}
                />
                {b.gate ? (
                  <span
                    title={b.gate.met ? 'Exit gate met' : 'Exit gate'}
                    className={cn('absolute top-2.5 size-3 -translate-x-1/2 rotate-45 border-2 border-card', b.gate.met ? 'bg-success' : 'bg-foreground')}
                    style={{ left: pct(b.gate.at) }}
                  />
                ) : null}
                {hover === b.key ? (
                  <span className="absolute -top-1 z-10 -translate-y-full whitespace-nowrap rounded bg-foreground px-2 py-1 text-tiny text-background shadow-md" style={{ left: pct(b.start) }}>
                    {shortDate(new Date(b.start).toISOString())} – {b.open ? 'ongoing' : shortDate(new Date(b.end).toISOString())}
                    {b.slipped ? ' · slipped' : ''}
                  </span>
                ) : null}
              </div>
            </div>
          ))}
          {milestones.some((m) => m.dueAt) ? (
            <div className="relative flex h-9 items-center">
              <div className="w-44 shrink-0 pr-2 text-sm font-medium">Milestones</div>
              <div className="relative h-full flex-1">
                {milestones.filter((m) => m.dueAt).map((m) => (
                  <span key={m.id} title={`${m.name} · ${longDate(m.dueAt)}`} className="absolute top-3 flex -translate-x-1/2 items-center" style={{ left: pct(Date.parse(m.dueAt!)) }}>
                    <span className={cn('size-3 rotate-45', m.doneAt ? 'bg-success' : 'bg-brand-accent')} />
                  </span>
                ))}
              </div>
            </div>
          ) : null}
        </div>
        <div className="ml-44 mt-2 flex flex-wrap gap-x-4 gap-y-1 text-tiny text-muted-foreground">
          <span className="inline-flex items-center gap-1"><span className="h-3 w-0.5 bg-danger" /> Today</span>
          {target ? <span className="inline-flex items-center gap-1"><span className="h-3 w-0 border-l-2 border-dashed border-brand" /> Target Post-Sales</span> : null}
          <span className="inline-flex items-center gap-1"><span className="size-2.5 rotate-45 bg-foreground" /> Exit gate</span>
          <span className="inline-flex items-center gap-1"><span className="size-2.5 rotate-45 bg-success" /> Gate met</span>
          <span className="inline-flex items-center gap-1"><span className="h-2.5 w-4 rounded-sm bg-danger" /> Slipped</span>
        </div>
      </div>
    </div>
  )
}
